import { Controller } from "@hotwired/stimulus"

// Handles drag-and-drop reordering of tasks within and across priority groups
export default class extends Controller {
  static targets = ["list", "item", "empty"]
  static values = {
    url: String,
    dayId: Number,
    priorityGroup: String,
    startDate: String
  }

  connect() {
    // Register this controller with the global drag manager
    if (!window.dragManager) {
      window.dragManager = {
        draggedItem: null,
        sourceController: null,
        controllers: new Set()
      }
    }
    window.dragManager.controllers.add(this)

    // Make the list itself a drop target for cross-group drops
    this.listTarget.addEventListener("dragover", this.handleListDragOver)
    this.listTarget.addEventListener("drop", this.handleListDrop)
    this.listTarget.addEventListener("dragenter", this.handleListDragEnter)
    this.listTarget.addEventListener("dragleave", this.handleListDragLeave)
  }

  disconnect() {
    window.dragManager?.controllers.delete(this)
    this.listTarget.removeEventListener("dragover", this.handleListDragOver)
    this.listTarget.removeEventListener("drop", this.handleListDrop)
    this.listTarget.removeEventListener("dragenter", this.handleListDragEnter)
    this.listTarget.removeEventListener("dragleave", this.handleListDragLeave)
  }

  // Called when an item connects to this controller
  itemTargetConnected(item) {
    item.addEventListener("dragstart", this.handleDragStart)
    item.addEventListener("dragend", this.handleDragEnd)
    item.addEventListener("dragover", this.handleDragOver)
    item.addEventListener("drop", this.handleDrop)
    item.addEventListener("dragenter", this.handleDragEnter)
    item.addEventListener("dragleave", this.handleDragLeave)
  }

  itemTargetDisconnected(item) {
    item.removeEventListener("dragstart", this.handleDragStart)
    item.removeEventListener("dragend", this.handleDragEnd)
    item.removeEventListener("dragover", this.handleDragOver)
    item.removeEventListener("drop", this.handleDrop)
    item.removeEventListener("dragenter", this.handleDragEnter)
    item.removeEventListener("dragleave", this.handleDragLeave)
  }

  // Arrow functions to preserve 'this' context
  handleDragStart = (event) => this.dragStart(event)
  handleDragEnd = (event) => this.dragEnd(event)
  handleDragOver = (event) => this.dragOver(event)
  handleDrop = (event) => this.drop(event)
  handleDragEnter = (event) => this.dragEnter(event)
  handleDragLeave = (event) => this.dragLeave(event)
  handleListDragOver = (event) => this.listDragOver(event)
  handleListDrop = (event) => this.listDrop(event)
  handleListDragEnter = (event) => this.listDragEnter(event)
  handleListDragLeave = (event) => this.listDragLeave(event)

  dragStart(event) {
    const item = event.target.closest("[data-sortable-target='item']")
    if (!item) return

    window.dragManager.draggedItem = item
    window.dragManager.sourceController = this

    item.classList.add("dragging")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", item.dataset.taskId)
    event.dataTransfer.setData("application/x-task-group", this.priorityGroupValue)
    event.dataTransfer.setData("application/x-day-id", this.dayIdValue)
  }

  dragEnd(event) {
    const item = window.dragManager.draggedItem
    if (item) {
      item.classList.remove("dragging")
    }
    window.dragManager.draggedItem = null
    window.dragManager.sourceController = null

    // Remove all drag-over styling from all controllers
    window.dragManager.controllers.forEach(controller => {
      controller.clearDragStyles()
    })
  }

  clearDragStyles() {
    this.itemTargets.forEach(item => {
      item.classList.remove("drag-over", "drag-over-top", "drag-over-bottom")
    })
    this.listTarget.classList.remove("drag-over-list")
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.remove("drag-over")
    }
  }

  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = "move"

    const target = event.target.closest("[data-sortable-target='item']")
    const draggedItem = window.dragManager.draggedItem
    if (!target || target === draggedItem) return

    const rect = target.getBoundingClientRect()
    const midpoint = rect.top + rect.height / 2

    // Clear other items' styles in this group
    this.itemTargets.forEach(item => {
      if (item !== target) {
        item.classList.remove("drag-over-top", "drag-over-bottom")
      }
    })

    // Show indicator for drop position
    target.classList.remove("drag-over-top", "drag-over-bottom")
    if (event.clientY < midpoint) {
      target.classList.add("drag-over-top")
    } else {
      target.classList.add("drag-over-bottom")
    }
  }

  dragEnter(event) {
    event.preventDefault()
    const target = event.target.closest("[data-sortable-target='item']")
    const draggedItem = window.dragManager.draggedItem
    if (target && target !== draggedItem) {
      target.classList.add("drag-over")
    }
  }

  dragLeave(event) {
    const target = event.target.closest("[data-sortable-target='item']")
    if (target) {
      const relatedTarget = event.relatedTarget?.closest("[data-sortable-target='item']")
      if (relatedTarget !== target) {
        target.classList.remove("drag-over", "drag-over-top", "drag-over-bottom")
      }
    }
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()

    const target = event.target.closest("[data-sortable-target='item']")
    const draggedItem = window.dragManager.draggedItem
    const sourceController = window.dragManager.sourceController

    if (!target || !draggedItem || target === draggedItem) return

    const rect = target.getBoundingClientRect()
    const midpoint = rect.top + rect.height / 2

    // Remove empty state if present
    if (this.hasEmptyTarget) {
      this.emptyTarget.remove()
    }

    // Insert the dragged item
    if (event.clientY < midpoint) {
      this.listTarget.insertBefore(draggedItem, target)
    } else {
      this.listTarget.insertBefore(draggedItem, target.nextSibling)
    }

    target.classList.remove("drag-over", "drag-over-top", "drag-over-bottom")

    // Save the new order
    this.saveOrderAfterDrop(sourceController)
  }

  // Handle drops on the list itself (for empty groups or at the end)
  listDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
  }

  listDragEnter(event) {
    event.preventDefault()
    // Only highlight if we're directly over the list, not an item
    if (event.target === this.listTarget || event.target.classList.contains("empty-priority-state")) {
      this.listTarget.classList.add("drag-over-list")
    }
  }

  listDragLeave(event) {
    // Only remove if we're leaving the list entirely
    if (!this.listTarget.contains(event.relatedTarget)) {
      this.listTarget.classList.remove("drag-over-list")
    }
  }

  listDrop(event) {
    // Only handle if dropped directly on list (not on an item)
    if (event.target.closest("[data-sortable-target='item']")) return

    event.preventDefault()
    event.stopPropagation()

    const draggedItem = window.dragManager.draggedItem
    const sourceController = window.dragManager.sourceController
    if (!draggedItem) return

    // Remove empty state if present
    if (this.hasEmptyTarget) {
      this.emptyTarget.remove()
    }

    // Append to end of list
    this.listTarget.appendChild(draggedItem)
    this.listTarget.classList.remove("drag-over-list")

    // Save the new order
    this.saveOrderAfterDrop(sourceController)
  }

  saveOrderAfterDrop(sourceController) {
    // Build a complete picture of all visible days and their groups
    // Group controllers by dayId
    const dayControllers = new Map()
    window.dragManager.controllers.forEach(controller => {
      const dayId = controller.dayIdValue
      if (!dayControllers.has(dayId)) {
        dayControllers.set(dayId, new Map())
      }
      dayControllers.get(dayId).set(controller.priorityGroupValue, controller)
    })

    // Build the days payload with all groups for each day
    const days = []
    dayControllers.forEach((groupControllers, dayId) => {
      const groups = {}
      ;["A", "B", "C"].forEach(group => {
        const controller = groupControllers.get(group)
        if (controller) {
          const taskIds = Array.from(controller.listTarget.querySelectorAll("[data-sortable-target='item']"))
            .map(item => item.dataset.taskId)
          groups[group] = taskIds
        } else {
          groups[group] = []
        }
      })
      days.push({ day_id: dayId, groups: groups })
    })

    // Add empty state back to source if it's now empty
    if (sourceController) {
      const sourceTaskIds = Array.from(sourceController.listTarget.querySelectorAll("[data-sortable-target='item']"))
      if (sourceTaskIds.length === 0 && !sourceController.listTarget.querySelector(".empty-priority-state")) {
        const emptyLi = document.createElement("li")
        emptyLi.className = "empty-priority-state"
        emptyLi.dataset.sortableTarget = "empty"
        emptyLi.textContent = `No ${sourceController.priorityGroupValue} tasks`
        sourceController.listTarget.appendChild(emptyLi)
      }
    }

    // Send to server
    const formData = new FormData()
    formData.append("start_date", this.startDateValue)
    formData.append("days", JSON.stringify(days))

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        console.error("Reorder failed:", response.status)
      }
      return response.text()
    })
    .then(html => {
      if (html) {
        Turbo.renderStreamMessage(html)
      }
    })
    .catch(error => {
      console.error("Reorder error:", error)
    })
  }
}
